import React, { useState, FC, ComponentPropsWithRef, useCallback } from 'react';
import keycode from 'keycode';
import whatInput from 'what-input';

import { cn, ClassName } from '../../utils';
import styles from './index.module.scss';


type FocusableDivProps = ClassName & Omit<ComponentPropsWithRef<'div'>, 'onClick' | 'onKeyPress' | 'onFocus' | 'onBlur'> & {
    onClick: React.ReactEventHandler<HTMLDivElement>;
    onKeyPress?: React.ReactEventHandler<HTMLDivElement>;
    onFocus?: React.FocusEventHandler<HTMLDivElement>;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    propagate?: boolean;
    focusedClassName?: string;
};

const isEnterOrSpace = (event: Event) =>
    keycode.isEventKey(event, 'enter') ||
    keycode.isEventKey(event, 'space');

export const FocusableDiv: FC<FocusableDivProps> =
    React.forwardRef<HTMLDivElement, FocusableDivProps>(({
        propagate, className, focusedClassName,
        onClick, onKeyPress, onFocus, onBlur, ...props
    }, ref) => {
        const [visualFocus, setVisualFocus] = useState(false);

        const onClickHandler: React.ReactEventHandler<HTMLDivElement> = useCallback(
            event => {
                if (!onClick)
                    return;
                onClick(event);
                if (!propagate)
                    event.stopPropagation();
            },
            [onClick, propagate],
        );

        const onKeyPressHandler: React.ReactEventHandler<HTMLDivElement> = useCallback(
            event => {
                if (isEnterOrSpace(event.nativeEvent) && (onClick || onKeyPress)) {
                    (onClick || onKeyPress)(event);
                    if (!propagate)
                        event.stopPropagation();
                }
            },
            [onClick, onKeyPress, propagate],
        );

        const onFocusHandler = useCallback(
            event => {
                if (whatInput.ask() === 'keyboard')
                    setVisualFocus(true);
                if (onFocus)
                    onFocus(event);
            },
            [onFocus],
        );

        const onBlurHandler = useCallback(
            event => {
                setVisualFocus(false);
                if (onBlur)
                    onBlur(event);
            },
            [onBlur],
        );

        return (
            <div
                ref={ref}
                className={cn(
                    styles.focusableDiv,
                    className,
                    visualFocus && focusedClassName,
                )}
                tabIndex={0}
                {...props}
                role='button'
                onClick={onClickHandler}
                onKeyPress={onKeyPressHandler}
                onFocus={onFocusHandler}
                onBlur={onBlurHandler}
            />
        );
    });
